import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { AsyncState } from '@/components/async';
import { Skeleton } from '@/components/feedback';
import { useDashboardOverviewStats } from '@/hooks/useDashboardOverviewStats';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';

interface ActionQueueItem {
  id: string;
  label: string;
  description: string;
  count: number;
  href: string;
}

export function DashboardActionQueue(): JSX.Element {
  const user = useDashboardUser();
  const statsQuery = useDashboardOverviewStats(user);

  return (
    <AsyncState
      data={statsQuery.data ?? null}
      isLoading={statsQuery.isLoading || statsQuery.isFetching}
      error={statsQuery.error}
      skeleton={
        <div className="grid gap-3 md:grid-cols-3">
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
        </div>
      }
    >
      {(stats) => {
        const items: ActionQueueItem[] = [
          {
            id: 'queue-showings',
            label: 'Showing requests',
            description: 'Approve or reschedule tours waiting on confirmation.',
            count: stats.showings.requested,
            href: '/dashboard/showings/requests',
          },
          {
            id: 'queue-alerts',
            label: 'Availability alerts',
            description: 'Review subscribers and pending notifications.',
            count: stats.availabilityAlerts.pendingEmails,
            href: '/dashboard/availability-alerts',
          },
          {
            id: 'queue-leads',
            label: 'Leads with tours',
            description: 'Follow up on leads with confirmed showings.',
            count: stats.showings.scheduledLeads,
            href: '/dashboard/leads',
          },
        ];

        const totalPending = items.reduce((sum, item) => sum + item.count, 0);

        return (
          <section className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-brand-accent">
                  Action queue
                </p>
                <p className="text-sm text-text-primary/70">
                  {totalPending > 0
                    ? `${totalPending} items need attention today.`
                    : 'All clear. No urgent actions right now.'}
                </p>
              </div>
              <span className="rounded-full bg-brand-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-primary">
                {totalPending > 0 ? `${totalPending} pending` : 'Clear'}
              </span>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              {items.map((item) => (
                <Link
                  key={item.id}
                  to={item.href}
                  className="flex flex-col gap-2 rounded-2xl border border-surface-subtle bg-white px-4 py-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-brand-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
                >
                  <p className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                    {item.label}
                  </p>
                  <p className="text-2xl font-semibold text-text-primary">
                    {item.count.toLocaleString()}
                  </p>
                  <p className="text-xs text-text-primary/70">{item.description}</p>
                </Link>
              ))}
            </div>
          </section>
        );
      }}
    </AsyncState>
  );
}
